import { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { updateCurrentUserProfile } from '../redux/CurrentUserReducer/currentUserSlice';
import { updateSearchedUserProfile } from '../redux/SearchedUserReducer/searchedUserSlice';
import { fetchSkills } from '../redux/SkillReducer/skillSlice';
import { fetchSkills as fetchTeams } from '../redux/TeamReducer/teamSlice';
import { ActivityLogEventActions } from '../constants/activityLogEventActions';
import {
    joinFullName,
    NAME_MAX_LENGTH,
    normalizeFullName,
    splitFullName,
    validateFullName,
} from '../utils/validationUtils';
import { toast } from 'sonner';

interface UseProfileFormProps {
    isHROrManager?: boolean;
    displayUser: any;
    searchedUser?: any;
}

export const useProfileForm = ({ isHROrManager, displayUser, searchedUser }: UseProfileFormProps) => {
    const dispatch = useAppDispatch();
    const { updating: currentUserUpdating, updateError: currentUserUpdateError } = useAppSelector((state) => state.currentUser);
    const { updating, updateError } = useAppSelector((state) => state.searchedUser);

    const [workdayId, setWorkdayId] = useState("");
    const [userName, setUserName] = useState(""); // single editable "Name" field
    const [site, setSite] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [userStatus, setUserStatus] = useState("");
    const [userSkills, setUserSkills] = useState<string[]>([]);
    const [userTeam, setUserTeam] = useState("");

    const [originalName, setOriginalName] = useState("");
    const [originalEmail, setOriginalEmail] = useState("");
    const [originalPhone, setOriginalPhone] = useState("");
    const [originalSkills, setOriginalSkills] = useState<string[]>([]);
    const [originalTeam, setOriginalTeam] = useState("");

    // Client-side validation of the single "Name" field
    const nameError = useMemo(() => validateFullName(userName), [userName]);

    const nameChanged = normalizeFullName(userName) !== normalizeFullName(originalName);
    const nonNameChanged =
        email !== originalEmail ||
        phone !== originalPhone ||
        userTeam !== originalTeam;

    // Save is enabled only when something has changed AND the name is valid.
    const hasChanges = (nameChanged || nonNameChanged) && !nameError;

    const hasSkillChanges = JSON.stringify([...userSkills].sort((a, b) => a.localeCompare(b))) !== JSON.stringify([...originalSkills].sort((a, b) => a.localeCompare(b)));

    useEffect(() => {
        if (site) {
            dispatch(fetchSkills(site));
            dispatch(fetchTeams());
        }
    }, [site, dispatch]);

    useEffect(() => {
        if (displayUser) {
            const fullName = joinFullName(displayUser.firstName, displayUser.lastName);

            setWorkdayId(displayUser.workdayId);
            setUserName(fullName);
            setSite(displayUser.siteCd);
            setUserEmail(displayUser.email);
            setEmail(displayUser.email);
            setPhone(displayUser.phoneNbr || "");
            setUserStatus(displayUser?.status || '');
            setUserTeam(displayUser.team || "");

            const skills = Array.isArray(displayUser.skills) ? displayUser.skills : [];
            setUserSkills(skills);

            setOriginalName(fullName);
            setOriginalEmail(displayUser.email);
            setOriginalPhone(displayUser.phoneNbr || "");
            setOriginalSkills(skills);
            setOriginalTeam(displayUser.team || "");
        }
    }, [displayUser]);

    useEffect(() => {
        if (updateError) {
            toast.error(`Failed to update profile: ${updateError}`);
        }
        if (currentUserUpdateError) {
            toast.error(`Failed to update profile: ${currentUserUpdateError}`);
        }
    }, [updateError, currentUserUpdateError]);

    const handlePhoneChange = (value: string) => {
        // Changed from replace(/\D/g, "") to replaceAll() per S7781
        const numericValue = value.replaceAll(/\D/g, "").slice(0, 10);
        let formatted = numericValue;
        if (numericValue.length > 6) {
            formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3, 6)}-${numericValue.slice(6)}`;
        } else if (numericValue.length > 3) {
            formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3)}`;
        }
        setPhone(formatted);
    };

    const handleSaveChanges = async () => {
        if (!displayUser) {
            toast.error("User data not available");
            return;
        }

        // Guard: email must not be cleared
        if (!email?.trim()) {
            toast.error("Email address cannot be empty.");
            setEmail(originalEmail);
            return;
        }

        // Guard: name must be valid
        if (nameError) {
            toast.error(nameError);
            return;
        }

        // Split the single "Name" field into firstName/lastName on the FIRST space.
        const { firstName, lastName } = splitFullName(userName);
        const normalizedName = normalizeFullName(userName);

        try {
            if (isHROrManager && searchedUser) {
                await dispatch(updateSearchedUserProfile({
                    workdayId: searchedUser.workdayId,
                    firstName,
                    lastName,
                    skills: userSkills,
                    team: userTeam,
                    eventAction: [ActivityLogEventActions.UserSkillAdded]
                })).unwrap();
                toast.success("Profile updated successfully!");
                setOriginalName(normalizedName);
                setUserName(normalizedName);
                setOriginalSkills([...userSkills]);
                setOriginalTeam(userTeam);
            } else {
                const actionMap = [
                    { changed: email !== originalEmail, action: ActivityLogEventActions.EmailAddressUpdated },
                    { changed: phone !== originalPhone, action: ActivityLogEventActions.PhoneNbrUpdated },
                    { changed: userTeam !== originalTeam, action: ActivityLogEventActions.UserSkillAdded }, // Reusing existing action or add new one
                ];
                const eventActions = actionMap.filter(e => e.changed).map(e => e.action);

                await dispatch(updateCurrentUserProfile({
                    workdayId: displayUser.workdayId,
                    firstName,
                    lastName,
                    email,
                    phoneNbr: phone,
                    team: userTeam,
                    eventAction: eventActions.length > 0 ? eventActions : undefined
                })).unwrap();
                toast.success("Profile updated successfully!");
                // Refresh baselines from the local edits; the slice already
                // replaces state.user with the API response so the header,
                // avatar initials, greetings, etc. reflect the new name.
                setOriginalName(normalizedName);
                setUserName(normalizedName);
                setOriginalEmail(email);
                setOriginalPhone(phone);
                setOriginalTeam(userTeam);
            }
        } catch (error) {
            console.error("Error updating profile:", error);
        }
    };

    // Guard: never allow the field to grow past NAME_MAX_LENGTH; validation
    // still catches malformed characters and empty values.
    const setUserNameBounded = (value: string) => {
        setUserName(value.slice(0, NAME_MAX_LENGTH));
    };

    return {
        workdayId,
        userName,
        setUserName: setUserNameBounded,
        nameError: nameError ?? undefined,
        site,
        userEmail,
        email,
        setEmail,
        phone,
        handlePhoneChange,
        userStatus,
        setUserStatus,
        userSkills,
        setUserSkills,
        userTeam,
        setUserTeam,
        hasChanges,
        hasSkillChanges,
        updating: isHROrManager ? updating : currentUserUpdating,
        handleSaveChanges,
    };
};